import { useState } from "react";
import { Tabs, Text } from "@mantine/core";
import { IconBarcode, IconCamera, IconChefHat, IconTextRecognition } from "@tabler/icons-react";
import { useSearchParams } from "react-router-dom";
import type { PassioFood } from "@passio/api-client";
import { PageHeader } from "../theme/components";
import { RequireFeature } from "../components/common";
import { FoodDetailDrawer } from "../components/food";
import { TextTour } from "./tours/TextTour";
import { PhotoTour } from "./tours/PhotoTour";
import { BarcodeTour } from "./tours/BarcodeTour";
import { RecipeTour } from "./tours/RecipeTour";
import type { TourFood } from "./tours/shared";

const TOURS = [
  { value: "text", label: "Log by text", icon: IconTextRecognition, blurb: "Typed meal → foods → hidden extras → glycemic load and processing → goal rating → shopper-friendly cart." },
  { value: "photo", label: "Snap a photo", icon: IconCamera, blurb: "Photo → foods → “did you mean…?” look-alikes → what the camera can't see → corrected log entry." },
  { value: "barcode", label: "Scan a product", icon: IconBarcode, blurb: "Barcode → product and label → processing score → glycemic load → goal rating." },
  { value: "recipe", label: "Recipe to cart", icon: IconChefHat, blurb: "Recipe text → structured meal → store units → Instacart cart." },
] as const;
type TourId = (typeof TOURS)[number]["value"];

/** Guided tours: each tab chains real Passio API calls, feeding one call's output into the next. */
export function TourPage() {
  const [params, setParams] = useSearchParams();
  const tab: TourId = TOURS.some((t) => t.value === params.get("tour")) ? (params.get("tour") as TourId) : "text";
  const [selected, setSelected] = useState<TourFood | null>(null);
  const [product, setProduct] = useState<PassioFood | null>(null);
  const tour = TOURS.find((t) => t.value === tab)!;

  return (
    <RequireFeature feature="text_ai">
      <PageHeader
        eyebrow="Guided tours · chained API calls"
        title="See the platform end to end"
        description="Each tour chains real Passio API calls the way an app would, feeding one call's output into the next. Open the request inspector (bottom right) to see every request, with code to copy."
      />
      <Tabs
        value={tab}
        onChange={(v) => {
          if (!v) return;
          const next = new URLSearchParams(params);
          next.set("tour", v);
          setParams(next, { replace: true });
        }}
        keepMounted
        variant="pills"
        radius="xl"
      >
        <Tabs.List mb="xs">
          {TOURS.map((t) => (
            <Tabs.Tab key={t.value} value={t.value} leftSection={<t.icon size={16} />}>
              {t.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        <Text fz="sm" c="dimmed" mb="lg">
          {tour.blurb}
        </Text>
        <Tabs.Panel value="text">
          <TextTour onSelect={setSelected} />
        </Tabs.Panel>
        <Tabs.Panel value="photo">
          <PhotoTour onSelect={setSelected} />
        </Tabs.Panel>
        <Tabs.Panel value="barcode">
          <BarcodeTour onOpen={setProduct} />
        </Tabs.Panel>
        <Tabs.Panel value="recipe">
          <RecipeTour onSelect={setSelected} />
        </Tabs.Panel>
      </Tabs>
      <FoodDetailDrawer refCode={selected?.refCode} name={selected?.displayName} amount={selected} onClose={() => setSelected(null)} />
      <FoodDetailDrawer data={product} onClose={() => setProduct(null)} />
    </RequireFeature>
  );
}
